"use client";

import { Link, Tile } from "@carbon/react";
import { Add, ChevronLeft, ChevronRight, Close, Settings } from "@carbon/react/icons";
import NextLink from "next/link";
import {
  startTransition,
  useEffect,
  useId,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from "react";
import { useFormStatus } from "react-dom";
import { cancelIndexingAction } from "@/app/(app)/documents/actions";
import {
  placeHomeWidgetAction,
  setHomeQuotaRangeAction,
  setHomeSpendRangeAction,
  setHomeWidgetConfigAction,
  toggleHomeWidgetAction,
} from "@/app/(app)/home-actions";
import {
  createChatScheduleAction,
  deleteChatScheduleAction,
  toggleChatScheduleAction,
  updateChatScheduleAction,
} from "@/app/(app)/schedule-actions";
import { FileTypeIcon } from "@/components/FileTypeIcon";
import { DocumentIndexStatus } from "@/components/DocumentIndexStatus";
import { formatSpendAmount, splitSpendFigure } from "@/lib/ai/spend-format";
import type { SpendRange, SpendSeriesView } from "@/lib/ai/spend-series";
import type { UserAiSpendRow } from "@/lib/ai/spend-users-view";
import type { ChatProviderId } from "@/lib/ai/settings-view";
import type { ChatScheduleView } from "@/lib/chat/schedule";
import { CHAT_SCHEDULE_MAX_PER_USER } from "@/lib/chat/schedule-time";
import type { HomeDocumentRow, HomeIndexingSnapshot } from "@/lib/home/documents";
import type { ServerResourcesView } from "@/lib/system/resources";
import { emptyDocuments } from "@/lib/ui/empty-state";
import { documentCollectionLabel } from "@/lib/ui/document-collection";
import { documentDepartmentLabel } from "@/lib/ui/document-home";
import { documentVersionLabel, reindexCopy } from "@/lib/ui/document-view";
import {
  indexingColumnsVisible,
  isConfigurableHomeWidget,
  optionalColumnsForWidget,
  recentColumnsVisible,
  resolveHomeWidgetConfigEntry,
  spendBlocksVisible,
  quotaBlocksVisible,
  spendUsersColumnsVisible,
  widthsForWidget,
  type ConfigurableHomeWidgetId,
  type HomeWidgetConfigEntry,
  type HomeWidgetConfigMap,
  type HomeWidgetWidth,
  type SpendOptionalBlock,
  type QuotaOptionalBlock,
} from "@/lib/ui/home-widget-config";
import {
  HOME_SCHEDULE_ROTATE_MS,
  homeScheduleAnswerSlides,
  homeWidgetCopy,
  homeWidgetEmptyCells,
  homeWidgetHint,
  homeWidgetOptionalColumnLabel,
  homeWidgetPlacements,
  isHomeWidgetId,
  nextHomeScheduleSlide,
  spendWalletBalanceLabel,
  spendServicesNote,
  type HomeLayoutToken,
  type HomeGridItemId,
  type HomeWidgetId,
} from "@/lib/ui/home-widgets";
import { showDocumentIndexBar } from "@/lib/ui/document-status";

const INDEX_POLL_MS = 2000;
const SERVER_POLL_MS = 3000;
const SCHEDULE_POLL_MS = 4000;
const HOME_WIDGET_DND = "text/plain";

type HomeDropTarget = HomeWidgetId | `slot:${number}:${number}`;

function widgetFromDrag(event: DragEvent): HomeWidgetId | null {
  const raw = event.dataTransfer?.getData(HOME_WIDGET_DND) ?? "";
  return isHomeWidgetId(raw) ? raw : null;
}

function parseDropTarget(value: string | null): HomeDropTarget | null {
  if (!value) {
    return null;
  }
  if (value.startsWith("slot:")) {
    const [, rowRaw, columnRaw] = value.split(":");
    const row = Number(rowRaw);
    const column = Number(columnRaw);
    return Number.isInteger(row) && Number.isInteger(column)
      ? `slot:${row}:${column}`
      : null;
  }
  return isHomeWidgetId(value) ? value : null;
}

function dropTargetFromEvent(event: DragEvent<HTMLDivElement>): HomeDropTarget | null {
  const raw = event.target;
  const node =
    raw instanceof Element
      ? raw
      : raw instanceof Node
        ? raw.parentElement
        : null;
  const drop = node?.closest("[data-drop]");
  if (!drop || !event.currentTarget.contains(drop)) {
    return null;
  }
  return parseDropTarget(drop.getAttribute("data-drop"));
}

function homeGridStyle(column: number, row: number, span: number) {
  return {
    ["--home-col" as string]: column,
    ["--home-row" as string]: row,
    ["--home-span" as string]: span,
  };
}

function DropHint({
  active,
  slot,
}: {
  active?: boolean;
  slot?: boolean;
}) {
  return (
    <div
      className={`home-widget-drop-hint${active ? " is-on" : ""}${slot ? " is-slot" : ""}`}
      aria-hidden="true"
    >
      {homeWidgetCopy.dropHere}
    </div>
  );
}

export type HomeSpendView = {
  provider: ChatProviderId;
  walletUsd: number | null;
  walletRub: number | null;
  model: string;
  thinkingLlm: boolean;
  thinkingWiki: boolean;
  seriesUsd: SpendSeriesView;
  seriesRub: SpendSeriesView;
  spentUsd: number;
  spentRub: number;
  bySourceUsd: { chat: number; index: number };
  bySourceRub: { chat: number; index: number };
};

export type HomeQuotaView = {
  provider: ChatProviderId;
  remainingUsd: number | null;
  remainingRub: number | null;
  limitUsd: number | null;
  limitRub: number | null;
  spentUsd: number;
  spentRub: number;
  seriesUsd: SpendSeriesView;
  seriesRub: SpendSeriesView;
};

function spendRangeLabel(range: SpendRange): string {
  if (range === "week") {
    return homeWidgetCopy.spendWeek;
  }
  if (range === "month") {
    return homeWidgetCopy.spendMonth;
  }
  return homeWidgetCopy.spendDay;
}

function HomeSpendDualChart({
  usd,
  rub,
  compact,
}: {
  usd: SpendSeriesView;
  rub: SpendSeriesView;
  compact?: boolean;
}) {
  return (
    <div
      className={`home-spend-chart-wrap is-dual${compact ? " is-compact" : ""}`}
    >
      <div className="home-spend-chart-y is-usd" aria-hidden="true">
        {usd.yLabels.map((label, index) => (
          <span key={`usd-${index}`}>{label}</span>
        ))}
      </div>
      <div className="home-spend-chart-plot">
        <svg
          className="home-spend-chart"
          viewBox="0 0 240 80"
          preserveAspectRatio="none"
          role="img"
          aria-label={`${spendRangeLabel(usd.range)}, ${homeWidgetCopy.spendUsd} и ${homeWidgetCopy.spendRub}`}
        >
          <path className="home-spend-chart-fill" d={usd.fillPath} />
          <path className="home-spend-chart-line" d={usd.linePath} />
          <path className="home-spend-chart-fill is-rub" d={rub.fillPath} />
          <path className="home-spend-chart-line is-rub" d={rub.linePath} />
        </svg>
        <div className="home-spend-chart-x" aria-hidden="true">
          {usd.xLabels.map((label, index) => (
            <span key={`${label}-${index}`}>{label}</span>
          ))}
        </div>
      </div>
      <div className="home-spend-chart-y is-rub is-end" aria-hidden="true">
        {rub.yLabels.map((label, index) => (
          <span key={`rub-${index}`}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function quotaMoneyLabel(
  amount: number | null,
  currency: "USD" | "RUB",
): string {
  return amount != null
    ? formatSpendAmount(amount, currency)
    : homeWidgetCopy.quotaUnlimited;
}

function HomeSpendRangeToggle({
  range,
  ranges,
  action,
}: {
  range: SpendRange;
  ranges: ReadonlyArray<{ value: SpendRange; label: string }>;
  action: (formData: FormData) => void | Promise<void>;
}) {
  return (
    <form action={action} className="home-spend-range home-lcd-row">
      {ranges.map((item) => (
        <button
          key={item.value}
          type="submit"
          name="range"
          value={item.value}
          className={`home-lcd-tag home-spend-range-btn${range === item.value ? " is-on" : ""}`}
        >
          {item.label}
        </button>
      ))}
    </form>
  );
}

const SPEND_RANGES = [
  { value: "day" as const, label: homeWidgetCopy.spendDay },
  { value: "week" as const, label: homeWidgetCopy.spendWeek },
  { value: "month" as const, label: homeWidgetCopy.spendMonth },
];

const QUOTA_RANGES = [
  { value: "day" as const, label: homeWidgetCopy.spendDay },
  { value: "week" as const, label: homeWidgetCopy.spendWeek },
  { value: "month" as const, label: homeWidgetCopy.spendMonth },
];

function HomeSpendConfig({ model }: { model: string }) {
  return (
    <div className="home-spend-config home-lcd-row">
      <span className="home-lcd-tag home-lcd-tag-model" title={model}>
        {model}
      </span>
    </div>
  );
}

function HomeSpendThinkingSegments({
  llm,
  wiki,
}: {
  llm: boolean;
  wiki: boolean;
}) {
  return (
    <div
      className="home-lcd-seg"
      role="group"
      aria-label={homeWidgetCopy.spendThinking}
    >
      <span className="home-lcd-seg-label">{homeWidgetCopy.spendThinking}</span>
      <span
        className={`home-lcd-seg-item${wiki ? " is-on" : ""}`}
        title={`${homeWidgetCopy.spendThinkWiki}: ${
          wiki
            ? homeWidgetCopy.spendThinkingOn
            : homeWidgetCopy.spendThinkingOff
        }`}
      >
        {homeWidgetCopy.spendThinkWiki}
      </span>
      <span
        className={`home-lcd-seg-item${llm ? " is-on" : ""}`}
        title={`${homeWidgetCopy.spendThinkLlm}: ${
          llm
            ? homeWidgetCopy.spendThinkingOn
            : homeWidgetCopy.spendThinkingOff
        }`}
      >
        {homeWidgetCopy.spendThinkLlm}
      </span>
    </div>
  );
}

function spendFigureClass(kind: "usd" | "rub", value: string): string {
  const money = /^[-]?[$₽]/.test(value);
  return `home-spend-figure is-${kind}${money ? "" : " is-text"}`;
}

function HomeSpendFigure({
  kind,
  value,
  hero,
}: {
  kind: "usd" | "rub";
  value: string;
  hero?: boolean;
}) {
  const parts = splitSpendFigure(value);
  const className = `${spendFigureClass(kind, value)}${hero ? " is-hero" : ""}`;
  if (!parts.currency) {
    return <div className={className}>{value}</div>;
  }
  return (
    <div className={className}>
      {parts.sign ? (
        <span className="home-spend-sign">{parts.sign}</span>
      ) : null}
      <span className="home-spend-ccy">{parts.currency}</span>
      <span className="home-spend-amt">{parts.amount}</span>
    </div>
  );
}

function HomeSpendProviderName({
  kind,
  name,
}: {
  kind: "usd" | "rub";
  name: string;
}) {
  return (
    <span className={`home-spend-provider is-${kind}`}>
      <span className="home-spend-total-swatch" aria-hidden="true" />
      {name}
    </span>
  );
}

function HomeKpiCard({
  kind,
  name,
  stats,
  hero,
  active,
}: {
  kind: "usd" | "rub";
  name: string;
  stats: Array<{
    label: string;
    value: string;
    note?: string | null;
    services?: string | null;
  }>;
  hero?: boolean;
  active?: boolean;
}) {
  if (stats.length === 0) {
    return null;
  }
  return (
    <article
      className={`home-kpi-card is-${kind}${hero ? " is-hero" : ""}${active ? " is-active" : ""}`}
      aria-current={active ? "true" : undefined}
    >
      <HomeSpendProviderName kind={kind} name={name} />
      <div className={`home-kpi-stats is-${stats.length}`}>
        {stats.map((stat) => (
          <div key={stat.label} className="home-kpi-stat">
            <p className="home-kpi-stat-label">{stat.label}</p>
            <HomeSpendFigure
              kind={kind}
              value={stat.value}
              hero={hero && stats.length === 1}
            />
            {stat.note ? (
              <p className="home-kpi-stat-note">{stat.note}</p>
            ) : null}
            {stat.services ? (
              <p className="home-kpi-services">{stat.services}</p>
            ) : null}
          </div>
        ))}
      </div>
    </article>
  );
}

function HomeKpiLane({
  usdStats,
  rubStats,
  hero,
  row,
}: {
  usdStats: Array<{
    label: string;
    value: string;
    note?: string | null;
    services?: string | null;
  }>;
  rubStats: Array<{
    label: string;
    value: string;
    note?: string | null;
    services?: string | null;
  }>;
  hero?: boolean;
  row?: boolean;
}) {
  return (
    <div
      className={`home-kpi-lane${hero ? " is-hero" : ""}${row ? " is-row" : ""}`}
    >
      <HomeKpiCard
        kind="usd"
        name={homeWidgetCopy.spendUsd}
        stats={usdStats}
        hero={hero}
      />
      <HomeKpiCard
        kind="rub"
        name={homeWidgetCopy.spendRub}
        stats={rubStats}
        hero={hero}
      />
    </div>
  );
}

function HomeSpendPanel({
  spend,
  blocks,
  wide,
}: {
  spend: HomeSpendView;
  blocks: Record<SpendOptionalBlock, boolean>;
  wide: boolean;
}) {
  const { model, thinkingLlm, thinkingWiki, seriesUsd, seriesRub } = spend;
  const walletUsd = spendWalletBalanceLabel(spend.walletUsd, "USD");
  const walletRub = spendWalletBalanceLabel(spend.walletRub, "RUB");
  const spentUsd = formatSpendAmount(spend.spentUsd, "USD");
  const spentRub = formatSpendAmount(spend.spentRub, "RUB");
  const usdServices = spendServicesNote(spend.bySourceUsd, "USD");
  const rubServices = spendServicesNote(spend.bySourceRub, "RUB");
  const showPrimary = blocks.balance;
  const showSecondary = blocks.spent;
  const showRange = showSecondary || blocks.chart;
  const split = wide && showPrimary && showSecondary && !blocks.chart;
  const usdStats = [
    ...(showPrimary
      ? [{ label: homeWidgetCopy.spendBalance, value: walletUsd }]
      : []),
    ...(showSecondary
      ? [
          {
            label: homeWidgetCopy.spendExpenses,
            value: spentUsd,
            services: usdServices,
          },
        ]
      : []),
  ];
  const rubStats = [
    ...(showPrimary
      ? [{ label: homeWidgetCopy.spendBalance, value: walletRub }]
      : []),
    ...(showSecondary
      ? [
          {
            label: homeWidgetCopy.spendExpenses,
            value: spentRub,
            services: rubServices,
          },
        ]
      : []),
  ];

  return (
    <div
      className={`home-spend${split ? " is-wide" : ""}${!wide ? " is-narrow" : ""}`}
    >
      {split ? (
        <HomeKpiLane
          usdStats={usdStats.slice(0, 1)}
          rubStats={rubStats.slice(0, 1)}
          hero
        />
      ) : null}
      <div className="home-spend-main">
        <div className="home-spend-toolbar">
          <HomeSpendThinkingSegments llm={thinkingLlm} wiki={thinkingWiki} />
          {showRange && !(split && showSecondary) ? (
            <HomeSpendRangeToggle
              range={seriesUsd.range}
              ranges={SPEND_RANGES}
              action={setHomeSpendRangeAction}
            />
          ) : null}
        </div>
        {!split && usdStats.length > 0 ? (
          <HomeKpiLane
            usdStats={usdStats}
            rubStats={rubStats}
            row
          />
        ) : null}
        {split && showSecondary ? (
          <div className="home-kpi-toolbar">
            <p className="home-spend-label">{homeWidgetCopy.spendExpenses}</p>
            {showRange ? (
              <HomeSpendRangeToggle
                range={seriesUsd.range}
                ranges={SPEND_RANGES}
                action={setHomeSpendRangeAction}
              />
            ) : null}
          </div>
        ) : null}
        {split && showSecondary ? (
          <div className="home-kpi-pair">
            <div className="home-kpi-stat">
              <HomeSpendFigure kind="usd" value={spentUsd} />
              <p className="home-kpi-services">{usdServices}</p>
            </div>
            <div className="home-kpi-stat">
              <HomeSpendFigure kind="rub" value={spentRub} />
              <p className="home-kpi-services">{rubServices}</p>
            </div>
          </div>
        ) : null}
        {blocks.model ? (
          <HomeSpendConfig model={model} />
        ) : blocks.chart ? (
          <div
            className="home-spend-config home-spend-config-slot"
            aria-hidden="true"
          />
        ) : null}
      </div>
      {blocks.chart ? (
        <HomeSpendDualChart usd={seriesUsd} rub={seriesRub} compact />
      ) : null}
    </div>
  );
}

function quotaCapNote(
  limit: number | null,
  currency: "USD" | "RUB",
): string | null {
  if (limit == null) {
    return null;
  }
  return `${homeWidgetCopy.quotaCap} ${formatSpendAmount(limit, currency)}`;
}

function HomeQuotaPanel({
  quota,
  blocks,
  wide,
}: {
  quota: HomeQuotaView;
  blocks: Record<QuotaOptionalBlock, boolean>;
  wide: boolean;
}) {
  const remainingUsd = quotaMoneyLabel(quota.remainingUsd, "USD");
  const remainingRub = quotaMoneyLabel(quota.remainingRub, "RUB");
  const limitUsd = quotaMoneyLabel(quota.limitUsd, "USD");
  const limitRub = quotaMoneyLabel(quota.limitRub, "RUB");
  const spentUsd = formatSpendAmount(quota.spentUsd, "USD");
  const spentRub = formatSpendAmount(quota.spentRub, "RUB");
  const usdNote = blocks.remaining && blocks.limit
    ? quotaCapNote(quota.limitUsd, "USD")
    : null;
  const rubNote = blocks.remaining && blocks.limit
    ? quotaCapNote(quota.limitRub, "RUB")
    : null;
  const showPrimary = blocks.remaining || blocks.limit;
  const showSecondary = blocks.spent;
  const primaryLabel = blocks.remaining
    ? homeWidgetCopy.configBlockRemaining
    : homeWidgetCopy.configBlockLimit;
  const primaryUsd = blocks.remaining ? remainingUsd : limitUsd;
  const primaryRub = blocks.remaining ? remainingRub : limitRub;
  const showRange = showSecondary || blocks.chart;
  const split = wide && showPrimary && showSecondary && !blocks.chart;
  const usdStats = [
    ...(showPrimary
      ? [{ label: primaryLabel, value: primaryUsd, note: usdNote }]
      : []),
    ...(showSecondary
      ? [{ label: homeWidgetCopy.quotaSpent, value: spentUsd }]
      : []),
  ];
  const rubStats = [
    ...(showPrimary
      ? [{ label: primaryLabel, value: primaryRub, note: rubNote }]
      : []),
    ...(showSecondary
      ? [{ label: homeWidgetCopy.quotaSpent, value: spentRub }]
      : []),
  ];

  return (
    <div
      className={`home-spend${split ? " is-wide" : ""}${!wide ? " is-narrow" : ""}`}
    >
      {split ? (
        <HomeKpiLane
          usdStats={usdStats.slice(0, 1)}
          rubStats={rubStats.slice(0, 1)}
          hero
        />
      ) : null}
      <div className="home-spend-main">
        {showRange && !(split && showSecondary) ? (
          <div className="home-spend-toolbar">
            <span className="home-spend-toolbar-spacer" aria-hidden="true" />
            <HomeSpendRangeToggle
              range={quota.seriesUsd.range}
              ranges={QUOTA_RANGES}
              action={setHomeQuotaRangeAction}
            />
          </div>
        ) : null}
        {!split && usdStats.length > 0 ? (
          <HomeKpiLane
            usdStats={usdStats}
            rubStats={rubStats}
            row
          />
        ) : null}
        {split && showSecondary ? (
          <div className="home-kpi-toolbar">
            <p className="home-spend-label">{homeWidgetCopy.quotaSpent}</p>
            {showRange ? (
              <HomeSpendRangeToggle
                range={quota.seriesUsd.range}
                ranges={QUOTA_RANGES}
                action={setHomeQuotaRangeAction}
              />
            ) : null}
          </div>
        ) : null}
        {split && showSecondary ? (
          <div className="home-kpi-pair">
            <HomeSpendFigure kind="usd" value={spentUsd} />
            <HomeSpendFigure kind="rub" value={spentRub} />
          </div>
        ) : null}
        {blocks.chart ? (
          <div
            className="home-spend-config home-spend-config-slot"
            aria-hidden="true"
          />
        ) : null}
      </div>
      {blocks.chart ? (
        <HomeSpendDualChart
          usd={quota.seriesUsd}
          rub={quota.seriesRub}
          compact
        />
      ) : null}
    </div>
  );
}

function HomeSpendUsersPanel({
  rows,
  showDepartment,
}: {
  rows: UserAiSpendRow[];
  showDepartment: boolean;
}) {
  if (rows.length === 0) {
    return (
      <p className="home-widget-empty">{homeWidgetCopy.emptySpendUsers}</p>
    );
  }
  return (
    <table className="home-widget-table home-widget-table-spendusers">
      <thead>
        <tr>
          <th scope="col">{homeWidgetCopy.colUser}</th>
          {showDepartment ? (
            <th scope="col">{homeWidgetCopy.colDepartment}</th>
          ) : null}
          <th scope="col" className="home-widget-table-num">
            {homeWidgetCopy.spendUsd}
          </th>
          <th scope="col" className="home-widget-table-num">
            {homeWidgetCopy.spendRub}
          </th>
          <th scope="col" className="home-widget-table-num">
            {homeWidgetCopy.quotaLimitUsd}
          </th>
          <th scope="col" className="home-widget-table-num">
            {homeWidgetCopy.quotaLimitRub}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td title={row.name}>{row.name}</td>
            {showDepartment ? (
              <td>{documentDepartmentLabel(row.departmentLabel || null)}</td>
            ) : null}
            <td className="home-widget-table-num">
              {formatSpendAmount(row.spentUsd, "USD")}
            </td>
            <td className="home-widget-table-num">
              {formatSpendAmount(row.spentRub, "RUB")}
            </td>
            <td className="home-widget-table-num">
              {row.kind === "wiki"
                ? homeWidgetCopy.quotaNone
                : quotaMoneyLabel(row.limitUsd, "USD")}
            </td>
            <td className="home-widget-table-num">
              {row.kind === "wiki"
                ? homeWidgetCopy.quotaNone
                : quotaMoneyLabel(row.limitRub, "RUB")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HomeServerMeter({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number;
  detail: string;
}) {
  return (
    <div className="home-server-meter">
      <div className="home-server-meter-row">
        <span className="home-server-meter-label">{label}</span>
        <span className="home-server-meter-value">{percent}%</span>
      </div>
      <div className="home-server-meter-track" aria-hidden="true">
        <div
          className="home-server-meter-fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="home-server-meter-detail">{detail}</p>
    </div>
  );
}

function HomeServerPanel({ initial }: { initial: ServerResourcesView }) {
  const [resources, setResources] = useState(initial);

  useEffect(() => {
    setResources(initial);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) {
        return;
      }
      try {
        const res = await fetch("/api/home/server", {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!res.ok || cancelled) {
          return;
        }
        const next = (await res.json()) as ServerResourcesView | null;
        if (!cancelled && next) {
          setResources(next);
        }
      } catch {
        /* ignore transient network errors while polling */
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, SERVER_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  return (
    <div className="home-server">
      <HomeServerMeter
        label={homeWidgetCopy.serverCpu}
        percent={resources.cpuPercent}
        detail="load / cores"
      />
      <HomeServerMeter
        label={homeWidgetCopy.serverMemory}
        percent={resources.memoryPercent}
        detail={`${resources.memoryUsedGb} / ${resources.memoryTotalGb} ГБ`}
      />
      <HomeServerMeter
        label={homeWidgetCopy.serverDisk}
        percent={resources.diskPercent}
        detail={`${resources.diskUsedGb} / ${resources.diskTotalGb} ГБ`}
      />
    </div>
  );
}

function HomeScheduleSettings({ rows }: { rows: ChatScheduleView[] }) {
  const atLimit = rows.length >= CHAT_SCHEDULE_MAX_PER_USER;
  return (
    <div className="home-schedule">
      {atLimit ? (
        <p className="home-schedule-meta">{homeWidgetCopy.scheduleLimit}</p>
      ) : (
        <form action={createChatScheduleAction} className="home-schedule-create">
          <label className="home-schedule-label" htmlFor="schedule-q">
            {homeWidgetCopy.scheduleQuestion}
          </label>
          <textarea
            id="schedule-q"
            name="question"
            className="home-schedule-input"
            rows={2}
            required
            maxLength={2000}
            placeholder="Вопрос…"
          />
          <div className="home-schedule-create-row">
            <label className="home-schedule-label" htmlFor="schedule-t">
              {homeWidgetCopy.scheduleTime}
            </label>
            <input
              id="schedule-t"
              name="timeLocal"
              type="time"
              className="home-schedule-time"
              required
              defaultValue="09:00"
            />
            <button type="submit" className="home-schedule-submit">
              {homeWidgetCopy.scheduleCreate}
            </button>
          </div>
        </form>
      )}
      {rows.length === 0 ? (
        <p className="home-widget-empty">{homeWidgetCopy.emptySchedule}</p>
      ) : (
        <ul className="home-schedule-list">
          {rows.map((row) => (
            <li key={row.id} className="home-schedule-item">
              <form action={updateChatScheduleAction} className="home-schedule-edit">
                <input type="hidden" name="id" value={row.id} />
                <textarea
                  name="question"
                  className="home-schedule-input"
                  rows={2}
                  required
                  maxLength={2000}
                  defaultValue={row.question}
                />
                <div className="home-schedule-create-row">
                  <input
                    name="timeLocal"
                    type="time"
                    className="home-schedule-time"
                    required
                    defaultValue={row.timeLocal}
                  />
                  <button type="submit" className="home-schedule-submit">
                    {homeWidgetCopy.scheduleSave}
                  </button>
                </div>
              </form>
              <p className="home-schedule-meta">
                {homeWidgetCopy.scheduleNext} {row.nextRunLabel}
                {row.lastRunLabel
                  ? ` · ${homeWidgetCopy.scheduleLast} ${row.lastRunLabel}`
                  : ""}
              </p>
              {row.lastError ? (
                <p className="home-schedule-error">{row.lastError}</p>
              ) : null}
              <div className="home-schedule-actions">
                {row.lastConversationHref ? (
                  <Link
                    href={row.lastConversationHref}
                    as={NextLink}
                    className="home-schedule-link"
                  >
                    {homeWidgetCopy.scheduleOpenChat}
                  </Link>
                ) : null}
                <form action={toggleChatScheduleAction}>
                  <input type="hidden" name="id" value={row.id} />
                  <input
                    type="hidden"
                    name="enabled"
                    value={row.enabled ? "false" : "true"}
                  />
                  <button type="submit" className="home-schedule-action">
                    {row.enabled
                      ? homeWidgetCopy.scheduleDisable
                      : homeWidgetCopy.scheduleEnable}
                  </button>
                </form>
                <form action={deleteChatScheduleAction}>
                  <input type="hidden" name="id" value={row.id} />
                  <button type="submit" className="home-schedule-action">
                    {homeWidgetCopy.scheduleDelete}
                  </button>
                </form>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function latestSchedule(rows: ChatScheduleView[]): ChatScheduleView | null {
  const withRun = rows.filter((row) => row.lastRunAtMs != null || row.running);
  if (withRun.length === 0) {
    return rows[0] ?? null;
  }
  return withRun.sort((left, right) => {
    if (left.running !== right.running) {
      return left.running ? -1 : 1;
    }
    return (right.lastRunAtMs ?? 0) - (left.lastRunAtMs ?? 0);
  })[0]!;
}

function scheduleWaitText(row: ChatScheduleView): string {
  if (row.running) {
    return homeWidgetCopy.scheduleRunning;
  }
  return `${homeWidgetCopy.scheduleWaiting} · ${row.nextRunLabel}`;
}

function HomeScheduleResult({
  row,
  slideIndex,
  slideCount,
  onPrev,
  onNext,
}: {
  row: ChatScheduleView | null;
  slideIndex: number;
  slideCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (!row) {
    return <p className="home-widget-empty">{homeWidgetCopy.emptySchedule}</p>;
  }
  return (
    <div className="home-schedule home-schedule-result">
      {row.lastError && !row.lastAnswer ? (
        <p className="home-schedule-error">{row.lastError}</p>
      ) : row.lastAnswer ? (
        <p className="home-schedule-answer">{row.lastAnswer}</p>
      ) : (
        <div className="home-schedule-answer-slot" />
      )}
      <div className="home-schedule-footer">
        <p className="home-schedule-wait">{scheduleWaitText(row)}</p>
        {slideCount > 1 ? (
          <div className="home-schedule-pager">
            <button
              type="button"
              className="home-schedule-pager-btn"
              aria-label={homeWidgetCopy.schedulePrevAnswer}
              onClick={onPrev}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="home-schedule-pager-count">
              {slideIndex + 1} / {slideCount}
            </span>
            <button
              type="button"
              className="home-schedule-pager-btn"
              aria-label={homeWidgetCopy.scheduleNextAnswer}
              onClick={onNext}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function HomeSchedulePanel({
  initial,
  settingsOpen,
}: {
  initial: ChatScheduleView[];
  settingsOpen: boolean;
}) {
  const [rows, setRows] = useState(initial);
  const [slideId, setSlideId] = useState<string | null>(null);
  const slides = homeScheduleAnswerSlides(rows);
  const slideIndex = Math.max(
    0,
    slides.findIndex((row) => row.id === slideId),
  );
  const slide = slides[slideIndex] ?? null;
  const slideKey = slides.map((row) => row.id).join(",");

  useEffect(() => {
    setRows(initial);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) {
        return;
      }
      try {
        const res = await fetch("/api/home/schedules", {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!res.ok || cancelled) {
          return;
        }
        const next = (await res.json()) as ChatScheduleView[];
        if (!cancelled) {
          setRows(next);
        }
      } catch {
        /* ignore transient network errors while polling */
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, SCHEDULE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const ids = slideKey.length === 0 ? [] : slideKey.split(",");
    if (ids.length === 0) {
      setSlideId(null);
      return;
    }
    if (!ids.includes(slideId ?? "")) {
      setSlideId(ids[0]!);
    }
  }, [slideKey, slideId]);

  useEffect(() => {
    const ids = slideKey.length === 0 ? [] : slideKey.split(",");
    if (ids.length < 2) {
      return;
    }
    const timer = window.setInterval(() => {
      setSlideId((current) => {
        const at = Math.max(0, ids.indexOf(current ?? ""));
        return ids[nextHomeScheduleSlide(at, ids.length, 1)]!;
      });
    }, HOME_SCHEDULE_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [slideKey, slideId]);

  function go(step: 1 | -1) {
    if (slides.length === 0) {
      return;
    }
    const next = nextHomeScheduleSlide(slideIndex, slides.length, step);
    setSlideId(slides[next]!.id);
  }

  if (settingsOpen) {
    return <HomeScheduleSettings rows={rows} />;
  }
  return (
    <HomeScheduleResult
      row={slide ?? latestSchedule(rows)}
      slideIndex={slideIndex}
      slideCount={slides.length}
      onPrev={() => go(-1)}
      onNext={() => go(1)}
    />
  );
}

function HomeRecentTable({
  rows,
  emptyTitle,
  emptyAction,
  columns,
}: {
  rows: HomeDocumentRow[];
  emptyTitle: string;
  emptyAction?: { href: string; label: string };
  columns: ReturnType<typeof recentColumnsVisible>;
}) {
  if (rows.length === 0) {
    return (
      <p className="home-widget-empty">
        {emptyTitle}
        {emptyAction ? (
          <>
            {" "}
            <Link href={emptyAction.href} as={NextLink}>
              {emptyAction.label}
            </Link>
          </>
        ) : null}
      </p>
    );
  }

  return (
    <table className="home-widget-table">
      <thead>
        <tr>
          <th scope="col">{homeWidgetCopy.colDocument}</th>
          {columns.type ? (
            <th scope="col">{homeWidgetCopy.colType}</th>
          ) : null}
          {columns.version ? (
            <th scope="col" className="home-widget-table-num">
              {homeWidgetCopy.colVersion}
            </th>
          ) : null}
          {columns.collection ? (
            <th scope="col">{homeWidgetCopy.colCollection}</th>
          ) : null}
          {columns.owner ? (
            <th scope="col">{homeWidgetCopy.colOwner}</th>
          ) : null}
          {columns.department ? (
            <th scope="col">{homeWidgetCopy.colDepartment}</th>
          ) : null}
          {columns.updated ? (
            <th scope="col">{homeWidgetCopy.colUpdated}</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.map((doc) => (
          <tr key={doc.id}>
            <td>
              <Link
                href={`/documents/${doc.id}`}
                as={NextLink}
                className="home-widget-row-title"
              >
                {doc.title}
              </Link>
            </td>
            {columns.type ? (
              <td className="home-widget-table-type">
                <FileTypeIcon type={doc.type} />
              </td>
            ) : null}
            {columns.version ? (
              <td className="home-widget-table-num">
                {documentVersionLabel(doc.version)}
              </td>
            ) : null}
            {columns.collection ? (
              <td>{documentCollectionLabel(doc.collectionName)}</td>
            ) : null}
            {columns.owner ? <td>{doc.owner}</td> : null}
            {columns.department ? (
              <td>{documentDepartmentLabel(doc.departmentName)}</td>
            ) : null}
            {columns.updated ? <td>{doc.updated}</td> : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StopIndexingButton({ className }: { className: string }) {
  const { pending } = useFormStatus();
  return (
    <button type="submit" className={className} disabled={pending}>
      {pending ? reindexCopy.stopPending : reindexCopy.stop}
    </button>
  );
}

function HomeIndexingTable({
  rows,
  columns,
}: {
  rows: HomeDocumentRow[];
  columns: ReturnType<typeof indexingColumnsVisible>;
}) {
  if (rows.length === 0) {
    return <p className="home-widget-empty">{homeWidgetCopy.emptyIndexing}</p>;
  }

  return (
    <table className="home-widget-table home-widget-table-index">
      <thead>
        <tr>
          <th scope="col">{homeWidgetCopy.colDocument}</th>
          {columns.collection ? (
            <th scope="col">{homeWidgetCopy.colCollection}</th>
          ) : null}
          {columns.owner ? (
            <th scope="col">{homeWidgetCopy.colOwner}</th>
          ) : null}
          {columns.version ? (
            <th scope="col" className="home-widget-table-num">
              {homeWidgetCopy.colVersion}
            </th>
          ) : null}
          <th scope="col" className="home-widget-table-progress">
            {homeWidgetCopy.colProgress}
          </th>
          <th scope="col" className="home-widget-table-index-head">
            <DocumentIndexStatus variant="head" />
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((doc) => (
          <tr key={doc.id}>
            <td>
              <Link
                href={`/documents/${doc.id}`}
                as={NextLink}
                className="home-widget-row-title"
              >
                {doc.title}
              </Link>
            </td>
            {columns.collection ? (
              <td>{documentCollectionLabel(doc.collectionName)}</td>
            ) : null}
            {columns.owner ? <td>{doc.owner}</td> : null}
            {columns.version ? (
              <td className="home-widget-table-num">
                {documentVersionLabel(doc.version)}
              </td>
            ) : null}
            <td className="home-widget-table-progress">
              {showDocumentIndexBar(doc.progress?.label) && doc.progress ? (
                <DocumentIndexStatus variant="progress" progress={doc.progress} />
              ) : (
                <span className="home-widget-row-meta">
                  {doc.progress?.label ?? "—"}
                </span>
              )}
            </td>
            <td className="home-widget-table-index-cell">
              <DocumentIndexStatus variant="marks" layers={doc.index} binary />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HomeIndexingList({
  initial,
  columns,
}: {
  initial: HomeIndexingSnapshot;
  columns: ReturnType<typeof indexingColumnsVisible>;
}) {
  const [view, setView] = useState(initial);

  useEffect(() => {
    setView(initial);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (document.hidden) {
        return;
      }
      try {
        const res = await fetch("/api/home/indexing", {
          credentials: "same-origin",
          cache: "no-store",
        });
        if (!res.ok || cancelled) {
          return;
        }
        const next = (await res.json()) as HomeIndexingSnapshot;
        if (!cancelled) {
          setView(next);
        }
      } catch {
        /* ignore transient network errors while polling */
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, INDEX_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const canStop = view.rows.some((row) => row.canWrite);

  return (
    <div className="home-index">
      <div className="home-index-summary">
        <div className="home-index-summary-meta">
          <span className="home-index-summary-model" title={view.model}>
            {view.model}
          </span>
          <span>
            {homeWidgetCopy.spendThinking}{" "}
            {view.thinking
              ? homeWidgetCopy.spendThinkingOn
              : homeWidgetCopy.spendThinkingOff}
          </span>
          {view.queueEta ? (
            <span>
              {homeWidgetCopy.indexingEta} {view.queueEta}
            </span>
          ) : null}
        </div>
        {canStop ? (
          <form action={cancelIndexingAction}>
            <StopIndexingButton className="home-index-stop" />
          </form>
        ) : null}
      </div>
      <HomeIndexingTable rows={view.rows} columns={columns} />
    </div>
  );
}

const HOME_WIDGET_WIDTH_LABELS: Record<HomeWidgetWidth, string> = {
  1: homeWidgetCopy.configWidth1,
  2: homeWidgetCopy.configWidth2,
  3: homeWidgetCopy.configWidth3,
};

function HomeWidgetConfigPanel({
  id,
  entry,
  onClose,
}: {
  id: ConfigurableHomeWidgetId;
  entry: HomeWidgetConfigEntry;
  onClose: () => void;
}) {
  const [units, setUnits] = useState<HomeWidgetWidth>(entry.units);
  const [columns, setColumns] = useState<string[]>(entry.columns);
  const optionalColumns = optionalColumnsForWidget(id);
  const widths = widthsForWidget(id);

  useEffect(() => {
    setUnits(entry.units);
    setColumns(entry.columns);
  }, [entry]);

  function onUnitsChange(next: HomeWidgetWidth) {
    setUnits(next);
  }

  function toggleColumn(column: string) {
    setColumns((current) =>
      current.includes(column)
        ? current.filter((item) => item !== column)
        : [...current, column],
    );
  }

  return (
    <form action={setHomeWidgetConfigAction} className="home-widget-config">
      <input type="hidden" name="widgetId" value={id} />
      <input type="hidden" name="units" value={units} />
      {columns.map((column) => (
        <input key={column} type="hidden" name="columns" value={column} />
      ))}
      <fieldset className="home-widget-config-section">
        <legend className="home-widget-config-label">
          {homeWidgetCopy.configWidth}
        </legend>
        <div className="home-widget-config-width">
          {widths.map((value) => (
            <label key={value} className="home-widget-config-choice">
              <input
                type="radio"
                name="units-ui"
                checked={units === value}
                onChange={() => onUnitsChange(value)}
              />
              <span>{HOME_WIDGET_WIDTH_LABELS[value]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {optionalColumns.length > 0 ? (
        <fieldset className="home-widget-config-section">
          <legend className="home-widget-config-label">
            {id === "spend" || id === "quota"
              ? homeWidgetCopy.configBlocks
              : homeWidgetCopy.configColumns}
          </legend>
          <div className="home-widget-config-columns">
            {optionalColumns.map((column) => (
              <label key={column} className="home-widget-config-choice">
                <input
                  type="checkbox"
                  checked={columns.includes(column)}
                  onChange={() => toggleColumn(column)}
                />
                <span>{homeWidgetOptionalColumnLabel(column)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
      <div className="home-widget-config-actions">
        <button type="submit" className="home-widget-config-save">
          {homeWidgetCopy.scheduleSave}
        </button>
        <button
          type="button"
          className="home-widget-config-cancel"
          onClick={onClose}
        >
          {homeWidgetCopy.configCancel}
        </button>
      </div>
    </form>
  );
}

function WidgetFrame({
  id,
  title,
  dragging,
  dropReady,
  settingsOpen,
  onDragStart,
  onDragEnd,
  onToggleSettings,
  children,
}: {
  id: HomeGridItemId;
  title?: string;
  dragging: boolean;
  dropReady: boolean;
  settingsOpen?: boolean;
  onDragStart: (event: DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
  onToggleSettings?: () => void;
  children: ReactNode;
}) {
  return (
    <Tile
      className={`home-widget-tile${dragging ? " is-dragging" : ""}${dropReady ? " is-drop" : ""}`}
      data-widget-id={id}
    >
      <div className="home-widget-tile-head">
        {/* Drag handle: native button so HTML5 drag events work. */}
        <button
          type="button"
          className="home-widget-handle"
          draggable={true}
          aria-label={homeWidgetCopy.moveWidget}
          title={homeWidgetCopy.moveWidget}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <span className="home-widget-handle-grip" aria-hidden="true" />
        </button>
        <h2 className="home-widget-tile-title">{title ?? homeWidgetCopy[id]}</h2>
        <div className="home-widget-tile-actions">
          {onToggleSettings ? (
            <button
              type="button"
              className={`home-widget-gear${settingsOpen ? " is-on" : ""}`}
              aria-label={homeWidgetCopy.scheduleSettings}
              aria-pressed={settingsOpen}
              onClick={onToggleSettings}
            >
              <Settings size={16} />
            </button>
          ) : null}
          <form action={toggleHomeWidgetAction.bind(null, id)}>
            <button
              type="submit"
              className="home-widget-close"
              aria-label={homeWidgetCopy.remove}
            >
              <Close size={16} />
            </button>
          </form>
        </div>
      </div>
      <div className="home-widget-tile-body">{children}</div>
    </Tile>
  );
}

function HomeWidgetSlot({
  row,
  column,
  span,
  dropReady,
  dragging,
  extra,
  hidden,
  open,
  onOpenChange,
}: {
  row: number;
  column: number;
  span: number;
  dropReady: boolean;
  dragging: boolean;
  extra: boolean;
  hidden: HomeWidgetId[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const canAdd = hidden.length > 0 && !dragging;

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div
      className={`home-dashboard-slot${dropReady ? " is-drop" : ""}${open ? " is-open" : ""}${canAdd ? " is-add" : ""}${extra ? " is-extra" : ""}`}
      style={homeGridStyle(column, row, span)}
      ref={rootRef}
      data-drop={`slot:${row}:${column}`}
    >
      {dragging ? <DropHint slot active={dropReady} /> : null}
      {canAdd ? (
        <>
          <button
            type="button"
            className="home-dashboard-slot-add"
            aria-label={homeWidgetCopy.addWidget}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => onOpenChange(!open)}
          >
            <Add size={28} aria-hidden="true" />
          </button>
          {open ? (
            <div id={menuId} className="home-widget-add-menu" role="menu">
              {hidden.map((id) => (
                <form
                  key={id}
                  action={placeHomeWidgetAction.bind(null, id, row, column)}
                  role="none"
                >
                  <button
                    type="submit"
                    className="home-widget-add-item"
                    role="menuitem"
                  >
                    <span className="home-widget-add-item-title">
                      {homeWidgetCopy[id]}
                    </span>
                    <span className="home-widget-add-item-hint">
                      {homeWidgetHint[id]}
                    </span>
                  </button>
                </form>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function widgetBody(
  id: HomeWidgetId,
  props: {
    recent: HomeDocumentRow[];
    indexing: HomeIndexingSnapshot;
    spend: HomeSpendView | null;
    quota: HomeQuotaView | null;
    spendUsers: UserAiSpendRow[];
    server: ServerResourcesView | null;
    schedules: ChatScheduleView[];
    settingsOpen: boolean;
    widgetConfig: HomeWidgetConfigMap;
    onCloseSettings: () => void;
    recentEmpty: {
      title: string;
      action?: { href: string; label: string };
    };
  },
): ReactNode {
  if (isConfigurableHomeWidget(id) && props.settingsOpen) {
    return (
      <HomeWidgetConfigPanel
        id={id}
        entry={resolveHomeWidgetConfigEntry(id, props.widgetConfig)}
        onClose={props.onCloseSettings}
      />
    );
  }
  if (id === "recent") {
    const entry = resolveHomeWidgetConfigEntry("recent", props.widgetConfig);
    return (
      <HomeRecentTable
        rows={props.recent}
        emptyTitle={props.recentEmpty.title}
        emptyAction={props.recentEmpty.action}
        columns={recentColumnsVisible(entry)}
      />
    );
  }
  if (id === "indexing") {
    const entry = resolveHomeWidgetConfigEntry("indexing", props.widgetConfig);
    return (
      <HomeIndexingList
        initial={props.indexing}
        columns={indexingColumnsVisible(entry)}
      />
    );
  }
  if (id === "spend" && props.spend) {
    const entry = resolveHomeWidgetConfigEntry("spend", props.widgetConfig);
    return (
      <HomeSpendPanel
        spend={props.spend}
        blocks={spendBlocksVisible(entry)}
        wide={entry.units >= 2}
      />
    );
  }
  if (id === "quota" && props.quota) {
    const entry = resolveHomeWidgetConfigEntry("quota", props.widgetConfig);
    return (
      <HomeQuotaPanel
        quota={props.quota}
        blocks={quotaBlocksVisible(entry)}
        wide={entry.units >= 2}
      />
    );
  }
  if (id === "spendusers") {
    const entry = resolveHomeWidgetConfigEntry("spendusers", props.widgetConfig);
    return (
      <HomeSpendUsersPanel
        rows={props.spendUsers}
        showDepartment={spendUsersColumnsVisible(entry).department}
      />
    );
  }
  if (id === "server" && props.server) {
    return <HomeServerPanel initial={props.server} />;
  }
  if (id === "schedule") {
    return (
      <HomeSchedulePanel
        initial={props.schedules}
        settingsOpen={props.settingsOpen}
      />
    );
  }
  return null;
}

type HomeDashboardProps = {
  layout: HomeLayoutToken[];
  hidden: HomeWidgetId[];
  widgetConfig: HomeWidgetConfigMap;
  recent: HomeDocumentRow[];
  indexing: HomeIndexingSnapshot;
  spend: HomeSpendView | null;
  quota: HomeQuotaView | null;
  spendUsers: UserAiSpendRow[];
  server: ServerResourcesView | null;
  schedules: ChatScheduleView[];
  seesAll: boolean;
};

export function HomeDashboard({
  layout,
  hidden,
  widgetConfig,
  recent,
  indexing,
  spend,
  quota,
  spendUsers,
  server,
  schedules,
  seesAll,
}: HomeDashboardProps) {
  const [draggingId, setDraggingId] = useState<HomeWidgetId | null>(null);
  const [overId, setOverId] = useState<HomeDropTarget | null>(null);
  const [settingsId, setSettingsId] = useState<HomeWidgetId | null>(null);
  const [addSlot, setAddSlot] = useState<string | null>(null);
  const draggingRef = useRef<HomeWidgetId | null>(null);
  const recentEmpty = seesAll
    ? {
        title: emptyDocuments.title,
        action: emptyDocuments.action,
      }
    : {
        title: homeWidgetCopy.emptyRecentOwn,
        action: emptyDocuments.action,
      };

  useEffect(() => {
    draggingRef.current = null;
    setDraggingId(null);
    setOverId(null);
    setAddSlot(null);
    setSettingsId(null);
  }, [layout, widgetConfig]);

  function toggleSettings(id: HomeWidgetId) {
    setSettingsId((current) => (current === id ? null : id));
  }

  function onWidgetDragStart(
    event: DragEvent<HTMLButtonElement>,
    id: HomeWidgetId,
  ) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(HOME_WIDGET_DND, id);
    const tile = event.currentTarget.closest(".home-widget-tile");
    if (tile instanceof HTMLElement) {
      event.dataTransfer.setDragImage(tile, 28, 20);
    }
    draggingRef.current = id;
    setAddSlot(null);
    requestAnimationFrame(() => {
      if (draggingRef.current === id) {
        setDraggingId(id);
      }
    });
  }

  function onWidgetDragEnd() {
    draggingRef.current = null;
    setDraggingId(null);
    setOverId(null);
  }

  function onGridDragOver(event: DragEvent<HTMLDivElement>) {
    if (!draggingRef.current) {
      return;
    }
    const target = dropTargetFromEvent(event);
    if (!target) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setOverId((current) => (current === target ? current : target));
  }

  function onGridDrop(event: DragEvent<HTMLDivElement>) {
    const target = dropTargetFromEvent(event);
    const fromId = widgetFromDrag(event) ?? draggingRef.current;
    draggingRef.current = null;
    setDraggingId(null);
    setOverId(null);
    if (!fromId || !target) {
      return;
    }
    event.preventDefault();
    if (target.startsWith("slot:")) {
      const [, rowRaw, columnRaw] = target.split(":");
      startTransition(() => {
        void placeHomeWidgetAction(fromId, Number(rowRaw), Number(columnRaw));
      });
      return;
    }
    if (!isHomeWidgetId(target)) {
      return;
    }
    const onto = homeWidgetPlacements(layout, false, widgetConfig).find(
      (item) => item.id === target,
    );
    if (!onto) {
      return;
    }
    startTransition(() => {
      void placeHomeWidgetAction(fromId, onto.row, onto.column);
    });
  }

  const placements = homeWidgetPlacements(layout, false, widgetConfig);
  const rowCount = Math.max(
    1,
    placements.reduce((max, item) => Math.max(max, item.row), 0),
  );
  const extraRow = draggingId != null || hidden.length > 0 ? 1 : 0;
  const slots = homeWidgetEmptyCells(
    placements.filter((item) => item.id !== draggingId),
    rowCount + extraRow,
  );

  return (
    <div
      className={`home-dashboard-grid${draggingId ? " is-dragging" : ""}`}
      role="list"
      onDragOver={onGridDragOver}
      onDrop={onGridDrop}
    >
      {placements.map((item) => {
        if (item.id === draggingId) {
          return null;
        }
        const dropReady = overId === item.id;
        return (
          <div
            key={item.id}
            className={`home-dashboard-cell${dropReady ? " is-drop" : ""}`}
            style={homeGridStyle(item.column, item.row, item.span)}
            role="listitem"
            data-drop={item.id}
          >
            <WidgetFrame
              id={item.id}
              dragging={false}
              dropReady={dropReady}
              settingsOpen={settingsId === item.id}
              onDragStart={(event) => {
                if (isHomeWidgetId(item.id)) {
                  onWidgetDragStart(event, item.id);
                }
              }}
              onDragEnd={onWidgetDragEnd}
              onToggleSettings={
                item.id === "schedule" || isConfigurableHomeWidget(item.id)
                  ? () => {
                      if (isHomeWidgetId(item.id)) {
                        toggleSettings(item.id);
                      }
                    }
                  : undefined
              }
            >
              {isHomeWidgetId(item.id)
                ? widgetBody(item.id, {
                    recent,
                    indexing,
                    spend,
                    quota,
                    spendUsers,
                    server,
                    schedules,
                    settingsOpen: settingsId === item.id,
                    widgetConfig,
                    onCloseSettings: () => setSettingsId(null),
                    recentEmpty,
                  })
                : null}
            </WidgetFrame>
            {draggingId && dropReady ? <DropHint active /> : null}
          </div>
        );
      })}
      {slots.map((slot) => {
        const key = `${slot.row}:${slot.column}`;
        return (
          <HomeWidgetSlot
            key={`slot-${key}`}
            row={slot.row}
            column={slot.column}
            span={slot.span}
            extra={slot.row > rowCount}
            dropReady={overId === `slot:${key}`}
            dragging={Boolean(draggingId)}
            hidden={hidden}
            open={addSlot === key}
            onOpenChange={(next) => setAddSlot(next ? key : null)}
          />
        );
      })}
    </div>
  );
}
